// The claim / tsumo action bar — a DOM overlay (not canvas) so its labels stay
// i18n-friendly and its sizing respects the user's font scale (rem units). It
// shows only when the local player has a decision: a Tsumo offer on their own
// winning draw, or Ron / Pung / Chi(+Skip) buttons during a claim window. Each
// tap hands a concrete `Claim | null` back to the flow driver.

import { clear, el } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import type { ChiOption, Claim, SeatClaims } from '../game/Claims.js';
import type { Seat, Tile } from '../game/types.js';
import type { KongDecision } from '../ai/types.js';
import { GREEN_FELT_THEME as THEME } from '../settings/themes.js';
import { tileName } from './tileName.js';

const CONTAINER_STYLE = [
  'position:fixed',
  'left:50%',
  'bottom:7.5rem',
  'transform:translateX(-50%)',
  'display:flex',
  'gap:0.75rem',
  'padding:0.5rem 0.75rem',
  'border-radius:0.75rem',
  'background:rgba(6,32,24,0.82)',
  'box-shadow:0 0.25rem 0.75rem rgba(0,0,0,0.4)',
  'z-index:20',
  'font-family:system-ui,-apple-system,sans-serif',
].join(';');

function buttonStyle(background: string, color: string): string {
  return [
    'min-width:4.5rem',
    'padding:0.6rem 1rem',
    'border:none',
    'border-radius:0.5rem',
    'font-size:1rem',
    'font-weight:600',
    'cursor:pointer',
    `background:${background}`,
    `color:${color}`,
  ].join(';');
}

const INK_DARK = '#1a1614';
const SKIP_BG = 'rgba(255,255,255,0.16)';

/** "Chi 4·5" — the two concealed ranks the run would use. */
function chiLabel(option: ChiOption): string {
  const ranks = option.tiles
    .map((tile) => (tile.kind === 'suit' ? String(tile.rank) : '?'))
    .join('·');
  return `${t('claim.chi')} ${ranks}`;
}

/** "Kong 4·Circles" for a concealed own-turn kong (names the specific tile);
 *  a plain "Kong" for the exposed/added cases, which always act on the one
 *  open discard / already-exposed pung, so there's no distinct tile to name. */
function kongLabel(kong: KongDecision): string {
  if (kong.kind !== 'concealed') return t('claim.kong');
  const displayTile: Tile = { ...kong.tile, id: 0 } as Tile;
  return `${t('claim.kong')} ${tileName(displayTile)}`;
}

export class ActionBar {
  private readonly root: HTMLElement;

  constructor() {
    this.root = el('div', { attrs: { style: `${CONTAINER_STYLE};display:none` } });
    document.body.appendChild(this.root);
  }

  /** The local seat's own turn offers Tsumo (if the draw completed the hand)
   *  and/or a kong button per available concealed/added kong option. */
  showOwnTurnOptions(
    canDeclareTsumo: boolean,
    kongOptions: readonly KongDecision[],
    onTsumo: () => void,
    onKong: (kong: KongDecision) => void,
  ): void {
    const buttons: HTMLElement[] = [];
    for (const kong of kongOptions) {
      buttons.push(this.button(kongLabel(kong), THEME.selectedRing, INK_DARK, () => onKong(kong)));
    }
    if (canDeclareTsumo) {
      buttons.push(this.button(t('claim.tsumo'), THEME.selectedRing, INK_DARK, onTsumo));
    }
    this.render(buttons);
  }

  /** The local seat may claim the open discard — one button per real option. */
  showClaimWindow(
    seat: Seat,
    claims: SeatClaims,
    onChoice: (claim: Claim | null) => void,
    onKong: () => void,
  ): void {
    const buttons: HTMLElement[] = [];
    if (claims.canRon) {
      buttons.push(
        this.button(t('claim.ron'), THEME.dragonRedColor, '#ffffff', () =>
          onChoice({ type: 'ron', seat }),
        ),
      );
    }
    if (claims.canPung) {
      buttons.push(
        this.button(t('claim.pung'), THEME.selectedRing, INK_DARK, () =>
          onChoice({ type: 'pung', seat }),
        ),
      );
    }
    if (claims.canKong) {
      buttons.push(this.button(t('claim.kong'), THEME.selectedRing, INK_DARK, onKong));
    }
    for (const option of claims.chi) {
      buttons.push(
        this.button(chiLabel(option), THEME.selectedRing, INK_DARK, () =>
          onChoice({ type: 'chi', seat, option }),
        ),
      );
    }
    buttons.push(this.button(t('claim.skip'), SKIP_BG, '#f4e6c0', () => onChoice(null)));
    this.render(buttons);
  }

  clear(): void {
    clear(this.root);
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.clear();
    this.root.remove();
  }

  private render(buttons: readonly HTMLElement[]): void {
    clear(this.root);
    for (const button of buttons) this.root.appendChild(button);
    this.root.style.display = 'flex';
  }

  private button(
    label: string,
    background: string,
    color: string,
    onClick: () => void,
  ): HTMLElement {
    return el('button', {
      text: label,
      attrs: { type: 'button', style: buttonStyle(background, color) },
      on: { click: onClick },
    });
  }
}
